import React, { useState, useEffect } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";
import "./Datasets.css";

const DatasetFormModal = ({ dataset, projects, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        project: "",
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const isEditing = !!dataset;

    useEffect(() => {
        if (dataset) {
            setFormData({
                name: dataset.name || "",
                description: dataset.description || "",
                project: dataset.project || "",
            });
        }
    }, [dataset]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error when field changes
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: null,
            }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                project: formData.project || null,
            };

            if (isEditing) {
                await API.patch(`/datasets/${dataset.id}/`, payload);
                toast.success("Dataset updated successfully.");
            } else {
                await API.post("/datasets/", payload);
                toast.success("Dataset created successfully.");
            }
            onSuccess();
        } catch (err) {
            console.error("Error saving dataset:", err);
            const errorMessage = err.response?.data?.detail ||
                                 err.response?.data?.name?.[0] ||
                                 "Failed to save dataset.";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="modal-backdrop-custom" onClick={handleBackdropClick}>
            <div className="modal-content-custom">
                <div className="modal-header-custom">
                    <h3>{isEditing ? "Edit Dataset" : "Create Dataset"}</h3>
                    <button className="modal-close-btn" onClick={onClose}>
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body-custom">
                        <div className="form-group">
                            <label className="form-label">Name *</label>
                            <input
                                type="text"
                                name="name"
                                className={`form-control ${errors.name ? "error" : ""}`}
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter dataset name"
                            />
                            {errors.name && (
                                <div className="error-text">{errors.name}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label">Description</label>
                            <textarea
                                name="description"
                                className="form-control"
                                rows="3"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Enter dataset description"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Project (Optional)</label>
                            <select
                                name="project"
                                className="form-control"
                                value={formData.project}
                                onChange={handleChange}
                            >
                                <option value="">-- No Project --</option>
                                {projects.map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.name}
                                    </option>
                                ))}
                            </select>
                            <small className="text-muted">
                                Assign this dataset to a project to enable collaboration
                            </small>
                        </div>
                    </div>

                    <div className="modal-footer-custom">
                        <button
                            type="button"
                            className="btn-secondary-custom"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-primary-custom"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Saving...
                                </>
                            ) : isEditing ? (
                                "Save Changes"
                            ) : (
                                "Create Dataset"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DatasetFormModal;
